import { useEffect, useState } from "react";

const today = () => new Date().toISOString().slice(0, 10);

async function api(path, options) {
  return fetch(path, { credentials: "same-origin", headers: { "Content-Type": "application/json" }, ...options });
}

const CPE_TYPE_LABEL = { group_a: "Group A", group_b: "Group B" };
const CBK_DOMAINS =
  "e.g. Security and Risk Management, Security Operations";

export function MemberCpe({ member, canManage }) {
  const [data, setData] = useState(null);
  const [form, setForm] = useState({
    credits: "",
    earned_on: today(),
    description: "",
    cpe_type: "group_a",
    cpe_domains: "",
  });
  const [error, setError] = useState("");

  function load() {
    api(`/api/members/${member.id}/cpe`)
      .then((res) => (res.ok ? res.json() : null))
      .then(setData);
  }
  useEffect(load, [member.id]);

  async function addEntry(e) {
    e.preventDefault();
    setError("");
    const res = await api(`/api/members/${member.id}/cpe`, { method: "POST", body: JSON.stringify(form) });
    if (res.ok) {
      setForm({ credits: "", earned_on: today(), description: "", cpe_type: "group_a", cpe_domains: "" });
      load();
    } else {
      setError((await res.json().catch(() => ({}))).error || "Could not add.");
    }
  }
  async function remove(entry) {
    await api(`/api/cpe/${entry.id}`, { method: "DELETE" });
    load();
  }

  if (data === null) return <p className="muted">Loading CPE…</p>;

  return (
    <div style={{ padding: "4px 2px" }}>
      <div style={{ marginBottom: 12 }}>
        <span style={{ fontSize: 22, fontWeight: 700 }}>{data.total}</span>
        <span className="muted"> CPE credits ({member.certifications || "no certs recorded"})</span>
        <span className="muted" style={{ marginLeft: 12, fontSize: 13 }}>
          Group A: {data.group_a} · Group B: {data.group_b}
        </span>
      </div>

      {data.certs && data.certs.length > 0 && (
        <div style={{ marginBottom: 12 }}>
          <div className="l" style={{ color: "var(--muted)", fontSize: 13, marginBottom: 6 }}>
            Certification requirements (Quorum-tracked CPE only)
          </div>
          {data.certs.map((c) => {
            const pct = Math.min(100, (c.tracked / c.annual_required) * 100);
            return (
              <div key={c.cert} style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
                <span style={{ width: 70, fontWeight: 600, fontSize: 13 }}>{c.cert}</span>
                <div className="bar" style={{ flex: 1 }}>
                  <span style={{ width: `${pct}%`, background: c.meets_annual ? "var(--green)" : undefined }} />
                </div>
                <span style={{ width: 160, textAlign: "right", fontSize: 12 }} className="muted">
                  {c.tracked} / {c.annual_required} annual · {c.cycle_total}/{c.cycle_years}yr
                </span>
              </div>
            );
          })}
        </div>
      )}

      {data.from_events.length > 0 && (
        <>
          <div className="l" style={{ color: "var(--muted)", fontSize: 13, marginBottom: 4 }}>From attended events</div>
          <table className="tbl" style={{ marginBottom: 12 }}>
            <tbody>
              {data.from_events.map((e, i) => (
                <tr key={i}>
                  <td>
                    {e.event}
                    {e.cpe_domains && (
                      <div className="muted" style={{ fontSize: 12 }}>{e.cpe_domains}</div>
                    )}
                  </td>
                  <td className="muted">{e.date}</td>
                  <td className="muted" style={{ fontSize: 12 }}>{CPE_TYPE_LABEL[e.cpe_type] || "Group A"}</td>
                  <td style={{ textAlign: "right" }}>{e.credits}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}

      <div className="l" style={{ color: "var(--muted)", fontSize: 13, marginBottom: 4 }}>Other / manual CPE</div>
      {data.manual.length ? (
        <table className="tbl" style={{ marginBottom: 12 }}>
          <tbody>
            {data.manual.map((m) => (
              <tr key={m.id}>
                <td>
                  {m.description || "—"}
                  {m.cpe_domains && (
                    <div className="muted" style={{ fontSize: 12 }}>{m.cpe_domains}</div>
                  )}
                </td>
                <td className="muted">{m.earned_on}</td>
                <td className="muted" style={{ fontSize: 12 }}>{CPE_TYPE_LABEL[m.cpe_type] || "Group A"}</td>
                <td style={{ textAlign: "right" }}>{m.credits}</td>
                {canManage && (
                  <td className="row-actions"><button className="btn-danger" type="button" onClick={() => remove(m)}>Remove</button></td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <p className="empty" style={{ marginBottom: 12 }}>No manual entries.</p>
      )}

      {canManage && (
        <form className="field-actions" onSubmit={addEntry} style={{ flexWrap: "wrap" }}>
          <input type="number" step="0.5" name="credits" placeholder="Credits" required value={form.credits} onChange={(e) => setForm({ ...form, credits: e.target.value })} style={{ width: 100 }} />
          <input type="date" name="earned_on" value={form.earned_on} onChange={(e) => setForm({ ...form, earned_on: e.target.value })} style={{ width: "auto" }} />
          <input name="description" placeholder="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} style={{ flex: 1, minWidth: 160 }} />
          <select name="cpe_type" value={form.cpe_type} onChange={(e) => setForm({ ...form, cpe_type: e.target.value })} style={{ padding: "8px 10px" }}>
            <option value="group_a">Group A (domain-specific)</option>
            <option value="group_b">Group B (professional dev)</option>
          </select>
          <input name="cpe_domains" placeholder={CBK_DOMAINS} value={form.cpe_domains} onChange={(e) => setForm({ ...form, cpe_domains: e.target.value })} style={{ flex: 1, minWidth: 200 }} />
          <button className="btn-ghost" type="submit">Add CPE</button>
          {error && <span className="err">{error}</span>}
        </form>
      )}
    </div>
  );
}
